// ANGEL FLIGHT — pure math helpers.
//
// Uses only the JS `Math` standard library (NO Phaser) so it runs fast in node
// tests and is the single home for the numeric utilities every system shares.
// Angles are radians; positions/velocities are plain numbers.

import type { Vec2 } from './types';
import type { Rng } from './rng';

/** Clamp `v` to the inclusive [min, max] range. */
export function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

/** Linear interpolate from a→b by t (t is NOT clamped). */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Frame-rate-independent exponential smoothing toward `target`. `lambda` is the
 * smoothing rate (higher = snappier); `dt` is seconds. Equivalent to repeated
 * lerps but invariant to frame timing — use this, never raw `lerp(a,b,0.1)`.
 */
export function damp(
  current: number,
  target: number,
  lambda: number,
  dt: number,
): number {
  return lerp(target, current, Math.exp(-lambda * dt));
}

/** Step `current` toward `target` by at most `maxDelta` (linear, no overshoot). */
export function approach(
  current: number,
  target: number,
  maxDelta: number,
): number {
  const d = target - current;
  if (Math.abs(d) <= maxDelta) return target;
  return current + Math.sign(d) * maxDelta;
}

/** Angle (radians) from point A→B, atan2 convention. */
export function angleTo(ax: number, ay: number, bx: number, by: number): number {
  return Math.atan2(by - ay, bx - ax);
}

/** Squared distance between two points (cheap — skips the sqrt). */
export function dist2(ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  return dx * dx + dy * dy;
}

/** Magnitude of a vector. */
export function lengthV(v: Vec2): number {
  return Math.hypot(v.x, v.y);
}

/**
 * Unit vector of `v`. Returns {0,0} for a zero-length input (safe — never NaN).
 * Allocates a new Vec2.
 */
export function normalize(v: Vec2): Vec2 {
  const len = Math.hypot(v.x, v.y);
  if (len === 0) return { x: 0, y: 0 };
  return { x: v.x / len, y: v.y / len };
}

/**
 * A random angular offset within ±`coneHalfRad`, drawn from `rng`. The spread
 * cone model: pass the weapon's current half-cone; result is added to the aim
 * angle. Uniform across the cone.
 */
export function spreadAngle(rng: Rng, coneHalfRad: number): number {
  return rng.range(-coneHalfRad, coneHalfRad);
}

/** Velocity components for moving along `angle` (radians) at `speed`. */
export function velocityFromAngle(angle: number, speed: number): Vec2 {
  return { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed };
}
